"use client"

import gsap from "gsap/all"
import { useParallax } from "hooks/useParallax"
import IlloBgIMG from "images/brokers/quoting/Background.png"
import StaticImage from "library/StaticImage"
import useCanHover from "library/canHover"
import { css, f, styled } from "library/styled"
import { useState } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"
import Steps from "./StepsData"

const MOVEMENT_PERCENT = -2

export default function QuotingDesktop() {
	const canHover = useCanHover()
	const [activeStep, setActiveStep] = useState(0)
	const { parallaxRef } = useParallax({
		movementPercent: MOVEMENT_PERCENT,
		canHover,
		additionalYOffset: MOVEMENT_PERCENT,
	})

	const handleStepChange = (index: number) => {
		if (!parallaxRef.current || index === activeStep) return

		gsap.to(parallaxRef.current, {
			opacity: 0,
			duration: 0.1,
			ease: "power1.out",
			onComplete: () => {
				setActiveStep(index)
				gsap.to(parallaxRef.current, {
					opacity: 1,
					duration: 0.25,
					ease: "power1.in",
					delay: 0.15,
				})
			},
		})
	}

	return (
		<Wrapper>
			<Inner>
				<IllustrationPanel>
					<IllustrationBg src={IlloBgIMG} width={892} height={800} alt="" />
					{Steps[activeStep]?.image && (
						<IllustrationFg
							ref={parallaxRef}
							src={Steps[activeStep]?.image}
							loading="eager"
							placeholder="empty"
							width={1092}
							height={1000}
							alt={Steps[activeStep]?.alt}
						/>
					)}
				</IllustrationPanel>
				<StepList>
					{Steps.map((step, index) => {
						const isActive = activeStep === index
						const Icon = step.icon

						return (
							<Step
								key={step.title}
								isActive={isActive}
								onClick={() => handleStepChange(index)}
							>
								<AbsoluteWrapper>
									<StepInner>
										<StepHeader isActive={isActive}>
											<StepIcon>
												<Icon />
											</StepIcon>
											<StepTitle>{step.title}</StepTitle>
										</StepHeader>
										<StepCopy isActive={isActive}>
											<SecondaryTitle>{step.title}</SecondaryTitle>
											<StepText>{step.text}</StepText>
										</StepCopy>
									</StepInner>
								</AbsoluteWrapper>
							</Step>
						)
					})}
				</StepList>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled("div", {
	...f.responsive(css`
		background: ${colors.green100Secondary};
		display: grid;
		place-items: center;
		width: 100%;
	`),
})

const Inner = styled("div", {
	...f.unresponsive(css`
		border: 1px solid ${colors.green400};
	`),
	...f.responsive(css`
		display: flex;
		margin: 0 auto;
		width: 1440px;
	`),
})

const IllustrationPanel = styled("div", {
	...f.unresponsive(css`
		border-right: 1px solid ${colors.green400};
	`),
	...f.responsive(css`
		position: relative;
		overflow: clip;
		width: 892px;
		height: 800px;
	`),
})

const illustrationBase = css`
	display: block;
	position: absolute;
	top: 0;
	left: 0;
`

const IllustrationFg = styled(StaticImage, {
	...f.responsive(css`
		${illustrationBase};
		width: 1092px;
		height: 1000px;
		top: -100px;
		left: -100px;
		max-inline-size: unset !important;
		max-block-size: unset !important;
	`),
	...f.tablet(css`
		display: none;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const IllustrationBg = styled(StaticImage, {
	...f.responsive(css`
		${illustrationBase};
		width: 892px;
		height: 800px;
	`),
	...f.tablet(css`
		display: none;
	`),
	...f.mobile(css`
		display: none;
	`),
})

const StepList = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		width: 548px;
	`),
})

const Step = styled("div", ({ isActive }: { isActive: boolean }) => {
	return {
		...f.unresponsive(css`
			border-bottom: 1px solid ${colors.green400};

			&:last-of-type {
				border-bottom: none;
			}
		`),
		...f.responsive(css`
			height: ${isActive ? "344px" : "114px"};
			transition: height 0.75s ease-in-out;
			position: relative;
			overflow: clip;
			cursor: pointer;
		`),
	}
})

const AbsoluteWrapper = styled("div", {
	...f.responsive(css`
		position: absolute;
		height: 344px;
		top: 0;
		left: 0;
	`),
})

const StepInner = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: 32px 64px 32px 32px;
		height: 100%;
	`),
})

const StepHeader = styled("div", ({ isActive }: { isActive: boolean }) => {
	return {
		...f.responsive(css`
			display: flex;
			align-items: center;
			gap: 16px;

			svg {
				path {
					transition: stroke 0.75s ease-in-out;
					stroke: ${isActive ? colors.greenAccent : colors.green400};
				}
			}

			h5 {
				transition: opacity 0.75s ease-in-out;
				opacity: ${isActive ? 0 : 1};
			}
		`),
	}
})

const StepIcon = styled("div", {
	...f.responsive(css`
		display: grid;
		place-items: center;
		width: 50px;
		height: 50px;
	`),
})

const StepTitle = styled("h5", {
	...f.responsive(css`
		${textStyles.bodyXL};
		color: ${colors.green400};
	`),
})

const StepCopy = styled("div", ({ isActive }: { isActive?: boolean }) => {
	return {
		...f.responsive(css`
			transition: all 0.75s ease-in-out;
			transform: ${isActive ? "translate(0, 0)" : "translate(50px, 30px)"};
			opacity: ${isActive ? 1 : 0};
		`),
	}
})

const SecondaryTitle = styled("h5", {
	...f.responsive(css`
		${textStyles.h5};
		color: ${colors.offWhite};
		width: 452px;
		margin-bottom: 24px;
	`),
})

const StepText = styled("p", {
	...f.responsive(css`
		${textStyles.bodyR};
		color: ${colors.green100};
		width: 436px;
	`),
})
